import { useCallback } from 'react';

import { zodResolver } from '@hookform/resolvers/zod';
import orderBy from 'lodash.orderby';
import { useForm } from 'react-hook-form';

import { dateTime, snackbar, useBoolean } from '@/_shared';

import { useCreateEmail } from './use-create-email';

import { dummyEmailsQuerySchema, type EmailFormSchema, type EmailQuerySchema, type EmailsProps } from '../shared';

export type EditProps = EmailsProps & { open: boolean; onClose: () => void };

export function useEdit({ open, value, onClose, onChange: parentOnChange }: EditProps) {
  const [isLoading, loading] = useBoolean();

  const form = useForm({
    resolver: zodResolver(dummyEmailsQuerySchema),
    defaultValues: { emails: value },
  });

  // keep dirty state on form update
  // and update form with sorted emails
  const updateFormWithKeepDirtyOn = useCallback(
    (emails: EmailFormSchema[]) => {
      const sortedEmails = orderBy(emails, (item) => item?.pivot?.default ?? 0, 'desc');
      form.setValue('emails', sortedEmails, { shouldDirty: true });
      return sortedEmails;
    },
    [form]
  );

  // handles defaults
  const handleDelete = useCallback(
    (email: EmailQuerySchema) => {
      const formEmails = form.getValues().emails;
      const newEmails = formEmails.filter((item) => item.id !== email.id);

      return updateFormWithKeepDirtyOn(newEmails);
    },
    [form, updateFormWithKeepDirtyOn]
  );

  const handleDefault = useCallback(
    (email: EmailQuerySchema) => {
      const formEmails = form.getValues().emails;
      const newEmails = formEmails.map((item) => {
        if (item.id === email.id || item.id === email.localMatchId) {
          return { ...item, ...email };
        } else {
          return { ...item, pivot: { ...item.pivot, default: 0 } };
        }
      });

      return updateFormWithKeepDirtyOn(newEmails);
    },
    [form, updateFormWithKeepDirtyOn]
  );

  const alreadyExistCheck = useCallback(
    (email: EmailQuerySchema) => {
      return [...new Set(form.getValues().emails.map((item) => item.email))].includes(email.email);
    },
    [form]
  );

  // will be triggered from create email form
  // after successful API response
  const handleCreateFromNewForm = useCallback(
    (email: EmailQuerySchema) => {
      if (alreadyExistCheck(email)) {
        console.log('handleCreateFromNewForm: Email already exists');
        return -2;
      }

      if (email.email !== '') {
        const formEmails = form.getValues().emails;
        const newEmails = [...formEmails, email];
        updateFormWithKeepDirtyOn(newEmails);
      }
      return 1;
    },
    [alreadyExistCheck, form, updateFormWithKeepDirtyOn]
  );

  // will be triggered from update email form
  // after successful API response
  const handleUpdateEmailItem = useCallback(
    (nEmail: EmailFormSchema) => {
      if (nEmail.intent === 'delete') {
        return handleDelete(nEmail);
      }
      if (nEmail.pivot.default === 1) {
        return handleDefault(nEmail);
      }

      // !important, to update latest values
      const fValues = form.getValues().emails;
      const nValue = fValues.map((item) => {
        // checking localMatchId
        // it is for create email case
        if (item.id === nEmail.id || item.id === nEmail.localMatchId) {
          return nEmail;
        }
        return item;
      });
      return updateFormWithKeepDirtyOn([...nValue]);
    },
    [form, handleDefault, handleDelete, updateFormWithKeepDirtyOn]
  );

  const createProps = useCreateEmail({ onChange: handleCreateFromNewForm });

  const checkNewFormValidity = useCallback(
    async (nonEmptyCheck = true) => {
      const newEmail = await createProps.validateForm();

      let issue = '';
      if (!newEmail) {
        issue = 'Invalid Email Address';
      } else if (alreadyExistCheck(newEmail)) {
        issue = 'Email already exists.';
      } else if (nonEmptyCheck && newEmail.email === '') {
        issue = 'Invalid Email Address';
      }

      if (issue) {
        createProps.form.setError('email', {
          type: 'manual',
          message: issue,
        });

        return { newEmail: undefined };
      }
      return { newEmail };
    },
    [alreadyExistCheck, createProps]
  );

  // does not submit just add into the update list
  // intent is to create
  // alternativeId should replaced by id
  const onAdd = useCallback(async () => {
    const { newEmail } = await checkNewFormValidity();

    if (newEmail) {
      updateFormWithKeepDirtyOn([...form.getValues().emails, { ...newEmail, localMatchId: newEmail.id }]);
      createProps.form.reset();
      createProps.form.setValue('id', dateTime.now().valueOf());
    }
  }, [checkNewFormValidity, createProps.form, form, updateFormWithKeepDirtyOn]);

  const onSubmit = useCallback(
    async (data: { emails: EmailFormSchema[] }) => {
      const { newEmail } = await checkNewFormValidity(false);
      if (!newEmail) {
        console.log('Invalid email or email already exists');
        return;
      }

      if (form.formState.isDirty) {
        loading.setTrue();
        parentOnChange(data.emails);
        loading.setFalse();
        onClose();
      } else {
        snackbar.error('No changes detected');
      }
    },
    [checkNewFormValidity, form.formState.isDirty, loading, parentOnChange, onClose]
  );

  // console.log(form.getValues().emails);

  return {
    open,
    form,
    isLoading,
    createProps,
    onClose,
    onSubmit,
    onAdd,
    handleUpdateEmailItem,
  };
}
